import React, { lazy, Suspense } from 'react';
import UpdateBanner from './components/shared/UpdateBanner';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { IconSettingsProvider } from './context/IconSettingsContext';
import { SectionColorProvider } from './context/SectionColorContext';
import { SeasonalThemeProvider } from './context/SeasonalThemeContext';
import Login from './components/Login';
import Layout from './components/Layout';
import ProtectedRoute from './components/ProtectedRoute';

import './App.css';

// Pages and heavy components load the first time their route is opened (smaller first download)
const InventoryHubPage = lazy(() => import('./pages/InventoryHubPage'));
const InventoryStockPage = lazy(() => import('./pages/InventoryStockPage'));
const InventoryMovementsPage = lazy(() => import('./pages/InventoryMovementsPage'));
const InventoryOrdersPage = lazy(() => import('./pages/InventoryOrdersPage'));
const InventoryReceiptsPage = lazy(() => import('./pages/InventoryReceiptsPage'));
const InventoryItemsPage = lazy(() => import('./pages/InventoryItemsPage'));
const Home = lazy(() => import('./pages/Home'));
const CompaniesPage = lazy(() => import("./pages/CompaniesPage"));
const CompanyBankAccountsPage = lazy(() => import("./pages/CompanyBankAccountsPage"));
const ClientsPage = lazy(() => import("./pages/ClientsPage"));
const PaymentConditionsPage = lazy(() => import("./pages/PaymentConditionsPage"));
const ConditionsPage = lazy(() => import("./pages/ConditionsPage"));
const AccountStatementsPage = lazy(() => import("./pages/AccountStatementsPage"));
const ReturnsPage = lazy(() => import("./pages/ReturnsPage"));
const TrucksPage = lazy(() => import("./pages/TrucksPage"));
const TrucksListPage = lazy(() => import("./pages/TrucksListPage"));
const ComissionsPage = lazy(() => import("./pages/ComissionsPage"));
const ComissionsListPage = lazy(() => import("./pages/ComissionsListPage"));
const StowagePage = lazy(() => import("./pages/StowagePage"));
const StowageListPage = lazy(() => import("./pages/StowageListPage"));
const PalletizingPage = lazy(() => import("./pages/PalletizingPage"));
const PalletizingListPage = lazy(() => import("./pages/PalletizingListPage"));
const EvaluationsPage = lazy(() => import("./pages/EvaluationsPage"));
const EvaluationsListPage = lazy(() => import("./pages/EvaluationsListPage"));
const ContainersPage = lazy(() => import("./pages/ContainersPage"));
const ContainersListPage = lazy(() => import("./pages/ContainersListPage"));
const CostChoicePage = lazy(() => import("./pages/CostChoicePage"));
const PriceChartPage = lazy(() => import('./pages/PriceChartPage'));
const ResumenCuadroPage = lazy(() => import('./pages/ResumenCuadroPage'));
const DirectorDashboardPage = lazy(() => import('./pages/DirectorDashboardPage'));
const AdminHubPage = lazy(() => import('./pages/AdminHubPage'));
const AccountsHubPage = lazy(() => import('./pages/AccountsHubPage'));
const ReportsHubPage = lazy(() => import('./pages/ReportsHubPage'));
const ClosingHubPage = lazy(() => import('./pages/ClosingHubPage'));
const InvoicingHubPage = lazy(() => import('./pages/InvoicingHubPage'));
const CostControlHubPage = lazy(() => import('./pages/CostControlHubPage'));
const FinancesHubPage = lazy(() => import('./pages/FinancesHubPage'));
const FinancialLoansPage = lazy(() => import('./pages/FinancialLoansPage'));
const FinancialLoansListPage = lazy(() => import('./pages/FinancialLoansListPage'));
const PriceChartListPage = lazy(() => import('./pages/PriceChartListPage'));
const PurchaseChartPage = lazy(() => import('./pages/PurchaseChartPage'));
const PurchaseChartListPage = lazy(() => import('./pages/PurchaseChartListPage'));
const CostChartPage = lazy(() => import('./pages/CostChartPage'));
const PackingListPage = lazy(() => import('./pages/PackingListPage'));
const PackingListsPage = lazy(() => import('./pages/PackingListsPage'));
const WeeklyClosingPage = lazy(() => import('./pages/WeeklyClosingPage'));
const CostChartListPage = lazy(() => import('./pages/CostChartListPage'));
const LocalBillingPage = lazy(() => import('./pages/LocalBillingPage'));
const LocalBillingListPage = lazy(() => import('./pages/LocalBillingListPage'));
const AnalyticsDashboard = lazy(() => import('./components/AnalyticsDashboard'));
const PaymentCalculator = lazy(() => import('./components/PaymentCalculator'));
const UserList = lazy(() => import('./components/UserList'));
const WhatsAppGroupsPage = lazy(() => import('./pages/WhatsAppGroupsPage'));
const ProfilePage = lazy(() => import('./components/ProfilePage'));
const SharePage = lazy(() => import('./pages/SharePage'));
const UnauthorizedPage = lazy(() => import('./components/UnauthorizedPage'));
const AIStatisticsChat = lazy(() => import('./components/AIStatisticsChat'));
const ActualizacionesPage = lazy(() => import('./pages/ActualizacionesPage'));
const OfficeModeGuard = lazy(() => import('./components/OfficeModeGuard'));
const ProducersPage = lazy(() => import('./pages/ProducersPage'));
const BrandsPage = lazy(() => import('./pages/BrandsPage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const ProductBillingPage = lazy(() => import('./pages/ProductBillingPage'));
const ProductTypesPage = lazy(() => import('./pages/ProductTypesPage'));
const BoxTypesPage = lazy(() => import('./pages/BoxTypesPage'));
const BillingPage = lazy(() => import('./pages/BillingPage'));
const BillingListPage = lazy(() => import('./pages/BillingListPage'));
const BillOfLadingPage = lazy(() => import('./pages/BillOfLadingPage'));
const WeeklySalesPricesPage = lazy(() => import('./pages/WeeklySalesPricesPage'));
const ShipmentPage = lazy(() => import('./pages/ShipmentPage'));
const SupplierManagementPage = lazy(() => import('./pages/SupplierManagementPage'));
const TrackingPage = lazy(() => import('./pages/TrackingPage'));
const OperationsHubPage = lazy(() => import('./pages/OperationsHubPage'));
const APIsHubPage = lazy(() => import('./pages/APIsHubPage'));
const ERPExplorerPage = lazy(() => import('./pages/ERPExplorerPage'));
const AllioExplorerPage = lazy(() => import('./pages/AllioExplorerPage'));
const CuadroPreciosPage = lazy(() => import('./pages/CuadroPreciosPage'));
const ShipmentsPage = lazy(() => import('./pages/ShipmentsPage'));
const ShipmentsListPage = lazy(() => import('./pages/ShipmentsListPage'));

function App() {
  return (
    <AuthProvider>
      <SeasonalThemeProvider>
      <IconSettingsProvider>
      <SectionColorProvider>
      <Router>
        {/* Each page's code downloads the first time it is opened (Layout shows the loader inside the page area) */}
        <Suspense fallback={null}>
        <Routes>
          {/* Public routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/unauthorized" element={<UnauthorizedPage />} />

          {/* Protected routes — all wrapped in Layout for sidebar */}
          <Route path="/" element={
            <ProtectedRoute>
              <Layout><Home /></Layout>
            </ProtectedRoute>
          } />

          {/* Admin-only routes */}
          <Route path="/settings" element={
            <ProtectedRoute requireAdmin={true}>
              <Layout><SettingsPage /></Layout>
            </ProtectedRoute>
          } />

          <Route path="/companies" element={
            <ProtectedRoute requireAdmin={true}>
              <Layout><CompaniesPage /></Layout>
            </ProtectedRoute>
          } />

          <Route path="/company-bank-accounts" element={
            <ProtectedRoute requireAdmin={true}>
              <Layout><CompanyBankAccountsPage /></Layout>
            </ProtectedRoute>
          } />

          <Route path="/product-billing" element={
            <ProtectedRoute requireAdmin={true}>
              <Layout><ProductBillingPage /></Layout>
            </ProtectedRoute>
          } />

          <Route path="/product-types" element={
            <ProtectedRoute requireAdmin={true}>
              <Layout><ProductTypesPage /></Layout>
            </ProtectedRoute>
          } />

          <Route path="/box-types" element={
            <ProtectedRoute requireAdmin={true}>
              <Layout><BoxTypesPage /></Layout>
            </ProtectedRoute>
          } />

          <Route path="/clients" element={
            <ProtectedRoute requireAdmin={true}>
              <Layout><ClientsPage /></Layout>
            </ProtectedRoute>
          } />

          <Route path="/payment-conditions" element={
            <ProtectedRoute requireAdmin={true}>
              <Layout><PaymentConditionsPage /></Layout>
            </ProtectedRoute>
          } />

          <Route path="/conditions" element={
            <ProtectedRoute requireAdmin={true}>
              <Layout><ConditionsPage /></Layout>
            </ProtectedRoute>
          } />

          <Route path="/producers" element={
            <ProtectedRoute requireAdmin={true}>
              <Layout><ProducersPage /></Layout>
            </ProtectedRoute>
          } />

          <Route path="/brands" element={
            <ProtectedRoute requireAdmin={true}>
              <Layout><BrandsPage /></Layout>
            </ProtectedRoute>
          } />

          {/* Transacciones / Visualizar Transacciones pages were retired —
              old bookmarks land on Estado de Cuenta instead of a 404. */}
          <Route path="/transactions" element={<Navigate to="/account-statements" replace />} />
          <Route path="/transactions/view" element={<Navigate to="/account-statements" replace />} />

          <Route path="/billing" element={
            <ProtectedRoute requireAdmin={true}>
              <Layout><BillingPage /></Layout>
            </ProtectedRoute>
          } />

          <Route path="/billing/list" element={
            <ProtectedRoute requireAdmin={true}>
              <Layout><BillingListPage /></Layout>
            </ProtectedRoute>
          } />

          <Route path="/bill-of-lading" element={
            <ProtectedRoute requireAdmin={true}>
              <Layout><BillOfLadingPage /></Layout>
            </ProtectedRoute>
          } />

          <Route path="/weekly-sales-prices" element={
            <ProtectedRoute requireAdmin={true}>
              <Layout><WeeklySalesPricesPage /></Layout>
            </ProtectedRoute>
          } />

          <Route path="/account-statements" element={
            <ProtectedRoute requireAdmin={true}>
              <Layout><AccountStatementsPage /></Layout>
            </ProtectedRoute>
          } />

          <Route path="/analytics" element={
            <ProtectedRoute>
              <Layout><AnalyticsDashboard /></Layout>
            </ProtectedRoute>
          } />

          <Route path="/payment-calculator" element={
            <ProtectedRoute>
              <Layout><PaymentCalculator /></Layout>
            </ProtectedRoute>
          } />

          {/* User Management Routes */}
          <Route path="/users" element={
            <ProtectedRoute requireAdmin={true}>
              <Layout><UserList /></Layout>
            </ProtectedRoute>
          } />

          <Route path="/users/create" element={<Navigate to="/users" replace />} />

          <Route path="/users/edit/:id" element={<Navigate to="/users" replace />} />

          <Route path="/whatsapp-groups" element={
            <ProtectedRoute requireAdmin={true}>
              <Layout><WhatsAppGroupsPage /></Layout>
            </ProtectedRoute>
          } />

          {/* a file shared from the phone's Share menu: choose the page it goes to (public/sw.js redirects here) */}
          <Route path="/share" element={
            <ProtectedRoute>
              <Layout><SharePage /></Layout>
            </ProtectedRoute>
          } />

          <Route path="/profile" element={
            <ProtectedRoute>
              <Layout><ProfilePage /></Layout>
            </ProtectedRoute>
          } />

          <Route path="/return" element={
            <ProtectedRoute>
              <Layout><ReturnsPage /></Layout>
            </ProtectedRoute>
          } />

          <Route path="/trucks" element={
            <ProtectedRoute>
              <Layout><TrucksPage /></Layout>
            </ProtectedRoute>
          } />

          <Route path="/trucks/list" element={
            <ProtectedRoute>
              <Layout><TrucksListPage /></Layout>
            </ProtectedRoute>
          } />

          <Route path="/comissions" element={
            <ProtectedRoute>
              <Layout><ComissionsPage /></Layout>
            </ProtectedRoute>
          } />

          <Route path="/comissions/list" element={
            <ProtectedRoute>
              <Layout><ComissionsListPage /></Layout>
            </ProtectedRoute>
          } />

          <Route path="/stowage" element={
            <ProtectedRoute>
              <Layout><StowagePage /></Layout>
            </ProtectedRoute>
          } />

          <Route path="/stowage/list" element={
            <ProtectedRoute>
              <Layout><StowageListPage /></Layout>
            </ProtectedRoute>
          } />

          <Route path="/palletizing" element={
            <ProtectedRoute>
              <Layout><PalletizingPage /></Layout>
            </ProtectedRoute>
          } />

          <Route path="/palletizing/list" element={
            <ProtectedRoute>
              <Layout><PalletizingListPage /></Layout>
            </ProtectedRoute>
          } />

          <Route path="/evaluations" element={
            <ProtectedRoute>
              <Layout><EvaluationsPage /></Layout>
            </ProtectedRoute>
          } />

          <Route path="/evaluations/list" element={
            <ProtectedRoute>
              <Layout><EvaluationsListPage /></Layout>
            </ProtectedRoute>
          } />

          <Route path="/containers" element={
            <ProtectedRoute>
              <Layout><ContainersPage /></Layout>
            </ProtectedRoute>
          } />

          <Route path="/containers/list" element={
            <ProtectedRoute>
              <Layout><ContainersListPage /></Layout>
            </ProtectedRoute>
          } />

          <Route path="/supplier" element={
            <ProtectedRoute>
              <Layout><SupplierManagementPage /></Layout>
            </ProtectedRoute>
          } />

          <Route path="/cost-choice" element={
            <ProtectedRoute>
              <Layout><CostChoicePage /></Layout>
            </ProtectedRoute>
          } />

          <Route path="/purchase-chart" element={
            <ProtectedRoute>
              <Layout><PurchaseChartPage /></Layout>
            </ProtectedRoute>
          } />

          <Route path="/purchase-chart/list" element={
            <ProtectedRoute>
              <Layout><PurchaseChartListPage /></Layout>
            </ProtectedRoute>
          } />

          <Route path="/price-chart" element={
            <ProtectedRoute>
              <Layout><PriceChartPage /></Layout>
            </ProtectedRoute>
          } />

          <Route path="/chart-summary" element={
            <ProtectedRoute>
              <Layout><ResumenCuadroPage /></Layout>
            </ProtectedRoute>
          } />

          <Route path="/director-dashboard" element={
            <ProtectedRoute>
              <Layout><DirectorDashboardPage /></Layout>
            </ProtectedRoute>
          } />

          {/* Snapshot de Cartera was retired: its numbers live in Analítica ▸ Semanas para cubrir */}
          <Route path="/portfolio-snapshot" element={<Navigate to="/analytics?vista=semanas-para-cubrir" replace />} />

          <Route path="/admin" element={
            <ProtectedRoute>
              <Layout><AdminHubPage /></Layout>
            </ProtectedRoute>
          } />

          <Route path="/accounts" element={
            <ProtectedRoute>
              <Layout><AccountsHubPage /></Layout>
            </ProtectedRoute>
          } />

          <Route path="/reports" element={
            <ProtectedRoute>
              <Layout><ReportsHubPage /></Layout>
            </ProtectedRoute>
          } />

          <Route path="/closing" element={
            <ProtectedRoute>
              <Layout><ClosingHubPage /></Layout>
            </ProtectedRoute>
          } />

          <Route path="/invoicing" element={
            <ProtectedRoute>
              <Layout><InvoicingHubPage /></Layout>
            </ProtectedRoute>
          } />

          <Route path="/cost-control" element={
            <ProtectedRoute>
              <Layout><CostControlHubPage /></Layout>
            </ProtectedRoute>
          } />

          <Route path="/overseas-payments" element={<Navigate to="/return" replace />} />

          <Route path="/inventory" element={
            <ProtectedRoute>
              <Layout><InventoryHubPage /></Layout>
            </ProtectedRoute>
          } />

          <Route path="/inventory/stock" element={
            <ProtectedRoute>
              <Layout><InventoryStockPage /></Layout>
            </ProtectedRoute>
          } />

          <Route path="/inventory/movements" element={
            <ProtectedRoute>
              <Layout><InventoryMovementsPage /></Layout>
            </ProtectedRoute>
          } />

          <Route path="/inventory/orders" element={
            <ProtectedRoute>
              <Layout><InventoryOrdersPage /></Layout>
            </ProtectedRoute>
          } />

          <Route path="/inventory/receipts" element={
            <ProtectedRoute>
              <Layout><InventoryReceiptsPage /></Layout>
            </ProtectedRoute>
          } />

          <Route path="/inventory/items" element={
            <ProtectedRoute>
              <Layout><InventoryItemsPage /></Layout>
            </ProtectedRoute>
          } />

          <Route path="/finances" element={
            <ProtectedRoute>
              <Layout><FinancesHubPage /></Layout>
            </ProtectedRoute>
          } />

          <Route path="/finances/loans" element={
            <ProtectedRoute>
              <Layout><FinancialLoansPage /></Layout>
            </ProtectedRoute>
          } />

          <Route path="/finances/loans/list" element={
            <ProtectedRoute>
              <Layout><FinancialLoansListPage /></Layout>
            </ProtectedRoute>
          } />

          <Route path="/price-chart/list" element={
            <ProtectedRoute>
              <Layout><PriceChartListPage /></Layout>
            </ProtectedRoute>
          } />

          <Route path="/cost-chart" element={
            <ProtectedRoute>
              <Layout><CostChartPage /></Layout>
            </ProtectedRoute>
          } />

          <Route path="/cost-chart/list" element={
            <ProtectedRoute>
              <Layout><CostChartListPage /></Layout>
            </ProtectedRoute>
          } />

          <Route path="/packing-list" element={
            <ProtectedRoute>
              <Layout><PackingListPage /></Layout>
            </ProtectedRoute>
          } />

          <Route path="/packing-list/list" element={
            <ProtectedRoute>
              <Layout><PackingListsPage /></Layout>
            </ProtectedRoute>
          } />

          <Route path="/weekly-closing" element={
            <ProtectedRoute>
              <Layout><WeeklyClosingPage /></Layout>
            </ProtectedRoute>
          } />

          <Route path="/local-billing" element={
            <ProtectedRoute>
              <Layout><LocalBillingPage /></Layout>
            </ProtectedRoute>
          } />

          <Route path="/local-billing/list" element={
            <ProtectedRoute>
              <Layout><LocalBillingListPage /></Layout>
            </ProtectedRoute>
          } />

          <Route path="/ai-stats-chat" element={
            <ProtectedRoute>
              <Layout><AIStatisticsChat /></Layout>
            </ProtectedRoute>
          } />

          <Route path="/shipment" element={
            <ProtectedRoute>
              <Layout><ShipmentPage /></Layout>
            </ProtectedRoute>
          } />

          <Route path="/tracking" element={
            <ProtectedRoute>
              <Layout><TrackingPage /></Layout>
            </ProtectedRoute>
          } />

          <Route path="/operations" element={
            <ProtectedRoute>
              <Layout><OperationsHubPage /></Layout>
            </ProtectedRoute>
          } />

          <Route path="/apis" element={
            <ProtectedRoute>
              <Layout><APIsHubPage /></Layout>
            </ProtectedRoute>
          } />

          <Route path="/erp-explorer" element={
            <ProtectedRoute>
              <Layout><ERPExplorerPage /></Layout>
            </ProtectedRoute>
          } />

          <Route path="/allio-explorer" element={
            <ProtectedRoute>
              <Layout><AllioExplorerPage /></Layout>
            </ProtectedRoute>
          } />

          <Route path="/price-board" element={
            <ProtectedRoute>
              <Layout><CuadroPreciosPage /></Layout>
            </ProtectedRoute>
          } />

          <Route path="/shipments" element={
            <ProtectedRoute>
              <Layout><ShipmentsPage /></Layout>
            </ProtectedRoute>
          } />

          <Route path="/shipments/list" element={
            <ProtectedRoute>
              <Layout><ShipmentsListPage /></Layout>
            </ProtectedRoute>
          } />

          <Route path="/updates" element={
            <ProtectedRoute>
              <Layout><ActualizacionesPage /></Layout>
            </ProtectedRoute>
          } />

          <Route path="/office" element={
            <ProtectedRoute>
              <Layout><OfficeModeGuard /></Layout>
            </ProtectedRoute>
          } />

          {/* Redirect to login if no route matches */}
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
        </Suspense>
      </Router>
      {/* "Nueva versión — Actualizar" when a newer deploy is live than the one this tab is running */}
      <UpdateBanner />
      </SectionColorProvider>
      </IconSettingsProvider>
      </SeasonalThemeProvider>
    </AuthProvider>
  );
}

export default App;
